/* LinkedInButton — uniforme LinkedIn-knop voor ELKE persoon in de app.
 *
 * Toont altijd een knop:
 *   • URL bekend   → blauwe LinkedIn-knop die het profiel opent.
 *   • URL onbekend + resolve prop → "Zoek profiel"-knop: de Profile Resolver
 *     agent zoekt live via Serper → Apollo en maakt de knop direct blauw.
 *   • Niet gevonden → gedempte "Niet gevonden op LinkedIn"-knop die (indien
 *     een naam meegegeven is) doorklikt naar de handmatige LinkedIn-zoek.
 *
 * resolve = { type: "candidate", id } of { type: "signal_contact", id, signalId }
 * Varianten: "pill" (detailpagina's), "icon" (compacte kaarten/tabellen),
 * "inline" (kleine tekstregels in lijsten).
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Linkedin, SearchX, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STYLES = {
    pill: {
        found: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#0a66c2]/40 bg-[#0a66c2]/10 text-[#0a66c2] hover:bg-[#0a66c2]/20 transition-colors text-xs",
        resolve: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--jr-amber)]/40 bg-[var(--jr-amber)]/10 text-[var(--jr-amber)] hover:bg-[var(--jr-amber)]/20 transition-colors text-xs",
        missing: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-[var(--jr-text-3)] hover:bg-white/[0.06] hover:text-[var(--jr-text-2)] transition-colors text-xs",
    },
    icon: {
        found: "inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0a66c2]/10 border border-[#0a66c2]/30 text-[#0a66c2] hover:bg-[#0a66c2]/20 transition-colors",
        resolve: "inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--jr-amber)]/10 border border-[var(--jr-amber)]/30 text-[var(--jr-amber)] hover:bg-[var(--jr-amber)]/20 transition-colors",
        missing: "inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/[0.03] border border-white/10 text-[var(--jr-text-3)] hover:bg-white/[0.06] transition-colors",
    },
    inline: {
        found: "inline-flex items-center gap-1 text-[#0a66c2] hover:text-white transition-colors",
        resolve: "inline-flex items-center gap-1 text-[var(--jr-amber)] hover:text-white transition-colors",
        missing: "inline-flex items-center gap-1 text-[var(--jr-text-3)] hover:text-[var(--jr-text-2)] transition-colors",
    },
};

const ICON_SIZE = { pill: 12, icon: 13, inline: 10 };

export const LinkedInButton = ({ url, name, variant = "inline", testid, className = "", resolve }) => {
    const { t } = useTranslation();
    const [liveUrl, setLiveUrl] = useState(url || null);
    const [busy, setBusy] = useState(false);
    const [tried, setTried] = useState(false);
    useEffect(() => { setLiveUrl(url || null); }, [url]);

    const iconPx = ICON_SIZE[variant];

    const runResolve = async (e) => {
        e.stopPropagation(); e.preventDefault();
        setBusy(true);
        try {
            const path = resolve.type === "signal_contact"
                ? `/profile-resolver/signal-contact/${resolve.signalId}/${resolve.id}`
                : `/profile-resolver/candidate/${resolve.id}`;
            const r = await api.post(path);
            if (r.data?.found && r.data?.url) {
                setLiveUrl(r.data.url);
                toast.success(t("common.linkedin_resolve_ok", { source: r.data.source }));
            } else {
                setTried(true);
                toast.info(t("common.linkedin_resolve_none"));
            }
        } catch (err) {
            setTried(true);
            toast.error(err?.response?.data?.detail || t("common.linkedin_resolve_none"));
        } finally { setBusy(false); }
    };

    // 1. Profiel bekend → blauwe LinkedIn-knop
    if (liveUrl) {
        return (
            <a href={liveUrl} target="_blank" rel="noreferrer" title={`LinkedIn: ${liveUrl}`}
                data-testid={testid || "linkedin-profile-btn"} onClick={(e) => e.stopPropagation()}
                className={`${STYLES[variant].found} ${className}`}>
                <Linkedin size={iconPx} />
                {variant !== "icon" && <span>{t("common.linkedin_profile")}</span>}
            </a>
        );
    }

    // 2. Onbekend + resolvebaar → "Zoek profiel" (Profile Resolver agent)
    if (resolve?.id && !tried) {
        return (
            <button type="button" onClick={runResolve} disabled={busy}
                title={t("common.linkedin_resolve_tip")}
                data-testid={testid ? `${testid}-resolve` : "linkedin-resolve-btn"}
                className={`${STYLES[variant].resolve} ${className} disabled:opacity-60`}>
                {busy ? <Loader2 size={iconPx} className="animate-spin" /> : <Sparkles size={iconPx} />}
                {variant !== "icon" && <span>{busy ? t("common.linkedin_resolving") : t("common.linkedin_resolve")}</span>}
            </button>
        );
    }

    // 3. Niet gevonden → gedempte knop, klikt door naar handmatige zoek
    const searchUrl = name
        ? `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(name)}`
        : null;
    const title = `${t("common.linkedin_not_found")}${searchUrl ? ` — ${t("common.linkedin_search_tip")}` : ""}`;
    const inner = (
        <>
            <SearchX size={iconPx} />
            {variant !== "icon" && <span>{t("common.linkedin_not_found")}</span>}
        </>
    );
    const cls = `${STYLES[variant].missing} ${className}`;
    const missingTestId = testid || "linkedin-not-found-btn";
    if (!searchUrl) {
        return <span data-testid={missingTestId} title={title} className={`${cls} cursor-default`}>{inner}</span>;
    }
    return (
        <a href={searchUrl} target="_blank" rel="noreferrer" data-testid={missingTestId} title={title}
            onClick={(e) => e.stopPropagation()} className={cls}>
            {inner}
        </a>
    );
};
